import { Component, inject, input, Signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FileDropComponent } from '@app/shared/components/files/file-drop/file-drop.component';
import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { FileDataService, FileUrlParams } from '@app/shared/services/file-data.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { FileContext } from '@app/shared/types/enums/file-enums';
import { UploadData } from '@app/shared/types/models/files/upload-data.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * @summary Overflow menu with the shared task row actions used by cards and the desktop table.
 *          On mobile the list is limited to time report, file, copy link/number and delete.
 * @selector proffeo-task-row-actions-menu
 */
@Component({
  selector: 'proffeo-task-row-actions-menu',
  templateUrl: './task-row-actions-menu.component.html',
  imports: [MatIconModule, MatMenuModule, FileDropComponent, TranslatePipe]
})
export class TaskRowActionsMenuComponent {
  private readonly taskActionsService = inject(TasksActionsService);
  private readonly dialogService = inject(DialogService);
  private readonly fileService = inject(FileDataService);
  private readonly deviceService = inject(DeviceService);

  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;

  /** @summary Task whose overflow actions are rendered. */
  public readonly task = input.required<Task>();

  protected onLockTask(): void {
    this.taskActionsService.lockTask(this.task());
  }

  protected onUnlockTask(): void {
    this.taskActionsService.unlockTask(this.task());
  }

  protected onDeleteTask(): void {
    this.taskActionsService.deleteTask(this.task()).subscribe();
  }

  protected onCopyLink(): void {
    this.taskActionsService.copyTaskLink(this.task());
  }

  protected onCopyTaskNumber(): void {
    this.taskActionsService.copyTaskNumber(this.task());
  }

  protected onCopyTask(): void {
    this.taskActionsService.copyTask(this.task());
  }

  protected onGeneratePdf(): void {
    this.dialogService.openGenerateTaskPdfDialog(this.task());
  }

  protected openMoveTaskDialog(): void {
    this.taskActionsService.openMoveTaskDialog(this.task());
  }

  protected openEditFinalCostDialog(): void {
    this.taskActionsService.openEditFinalCostDialog(this.task());
  }

  protected openEditContractCostDialog(): void {
    this.taskActionsService.openEditContractCostDialog(this.task());
  }

  protected openTimeReportDialog(): void {
    this.dialogService.openTimeReportDialog(this.task());
  }

  protected onFilesAdded(files: File[]): void {
    const uploadParams: FileUrlParams = {
      context: FileContext.taskFile,
      taskId: this.task()?.id
    };

    files.forEach(file => {
      const uploadData: UploadData = {
        file,
        description: '',
        fileTags: [],
        orderNo: 0
      };
      this.fileService.uploadFile(uploadParams, uploadData);
    });
  }
}
